import React, { Component } from "react";
import { Form, Button, ProgressBar, Alert, Badge } from "react-bootstrap";
import pptoService, { errorMessage } from "../lib/ppto-service";

const POLL_MS = 1000;
const JOB_KEY = "pptoUploadJob"; // para seguir el proceso si se recarga la página

const mb = (bytes) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;
const now = () => new Date().toLocaleTimeString("es-ES");

const STATUS = {
  running: { variant: "warning", text: "En curso" },
  done: { variant: "success", text: "Completado" },
  error: { variant: "danger", text: "Error" }
};

class PptoUpload extends Component {
  state = {
    files: [],
    fileIds: [],
    uploading: false,
    sent: 0,
    dryRun: false,
    jobId: null,
    job: null,
    log: [],
    error: ""
  };

  logRef = React.createRef();
  fileInputRef = React.createRef();

  componentDidMount() {
    let jobId = null;
    try {
      jobId = sessionStorage.getItem(JOB_KEY);
    } catch (e) {}
    if (jobId) this.setState({ jobId }, this.poll);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.log.length !== this.state.log.length && this.logRef.current) {
      this.logRef.current.scrollTop = this.logRef.current.scrollHeight;
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    clearTimeout(this.pollTimer);
  }

  addLog = (msg, level = "info") => {
    this.setState(({ log }) => ({ log: [...log, { time: now(), level, msg }] }));
  };

  handleFiles = (event) => {
    this.setState({ files: Array.from(event.target.files), fileIds: [], sent: 0, error: "" });
  };

  // Devuelve los ids de los ficheros subidos, o null si ha fallado
  handleUpload = async () => {
    const { files } = this.state;
    this.setState({ uploading: true, sent: 0, fileIds: [], error: "", job: null, jobId: null, log: [] });
    const fileIds = [];
    let done = 0;
    try {
      for (const file of files) {
        this.addLog(`Subiendo ${file.name} (${mb(file.size)})...`);
        const previous = done;
        const id = await pptoService.upload(file, (sent) => this.setState({ sent: previous + sent }));
        done += file.size;
        fileIds.push(id);
        this.addLog(`${file.name} subido correctamente`);
      }
      this.setState({ fileIds });
      return fileIds;
    } catch (error) {
      this.addLog(`ERROR al subir: ${errorMessage(error)}`, "error");
      this.setState({ error: errorMessage(error) });
      return null;
    } finally {
      this.setState({ uploading: false });
    }
  };

  // Si los ficheros aún no están subidos, los sube primero
  handleProcess = async () => {
    const { files, dryRun } = this.state;
    let { fileIds } = this.state;
    this.setState({ error: "" });
    if (fileIds.length !== files.length) {
      fileIds = await this.handleUpload();
      if (!fileIds) return;
    }
    try {
      const jobId = await pptoService.process(fileIds, dryRun);
      try {
        sessionStorage.setItem(JOB_KEY, jobId);
      } catch (e) {}
      // Los ficheros se borran del servidor al terminar: para repetir hay que volver a subirlos
      this.setState({ jobId, fileIds: [], job: { status: "running" } }, this.poll);
    } catch (error) {
      this.addLog(`ERROR al iniciar el proceso: ${errorMessage(error)}`, "error");
      this.setState({ error: errorMessage(error) });
    }
  };

  // Pide las líneas de log nuevas cada segundo hasta que el proceso termina
  poll = async () => {
    const { jobId } = this.state;
    if (!jobId || this.unmounted) return;
    try {
      const job = await pptoService.job(jobId, this.serverLines || 0);
      if (this.unmounted) return;
      this.serverLines = (this.serverLines || 0) + job.log.length;
      this.setState(({ log }) => ({ job, log: [...log, ...job.log] }));
      if (job.status === "running") {
        this.pollTimer = setTimeout(this.poll, POLL_MS);
      } else {
        this.serverLines = 0;
        try {
          sessionStorage.removeItem(JOB_KEY);
        } catch (e) {}
      }
    } catch (error) {
      if (this.unmounted) return;
      if (error.response && error.response.status === 404) {
        // El servidor se ha reiniciado o el proceso es antiguo
        try {
          sessionStorage.removeItem(JOB_KEY);
        } catch (e) {}
        this.setState({ jobId: null, job: null });
        return;
      }
      // Fallo puntual de red: se reintenta
      this.pollTimer = setTimeout(this.poll, POLL_MS * 3);
    }
  };

  handleCsv = async () => {
    const { jobId, job } = this.state;
    try {
      const blob = await pptoService.csv(jobId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${job.files[0].replace(/\.xlsx$/i, "")}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      this.setState({ error: errorMessage(error) });
    }
  };

  handleReset = () => {
    if (this.fileInputRef.current) this.fileInputRef.current.value = "";
    this.setState({ files: [], fileIds: [], sent: 0, jobId: null, job: null, log: [], error: "" });
  };

  renderResult() {
    const { job } = this.state;
    if (!job || job.status === "running") return null;
    if (job.status === "error") {
      return <Alert variant="danger">El proceso ha terminado con errores. Revisa el log.</Alert>;
    }
    if (job.dryRun) {
      return (
        <Alert variant="info">
          Simulación terminada: {job.rows} filas generadas. No se ha guardado nada en la base de datos.
        </Alert>
      );
    }
    const { summary } = job;
    return (
      <Alert variant="success">
        Presupuesto cargado correctamente: {job.rows} filas
        {summary && ` (${summary.upserted} nuevas, ${summary.modified} modificadas, ${summary.unchanged} sin cambios)`}.
      </Alert>
    );
  }

  render() {
    const { files, fileIds, uploading, sent, dryRun, job, log, error } = this.state;
    const total = files.reduce((sum, file) => sum + file.size, 0);
    const running = Boolean(job && job.status === "running");
    const uploaded = files.length > 0 && fileIds.length === files.length;
    const status = job && STATUS[job.status];

    return (
      <div className="ppto-upload">
        <h1 className="ppto-upload-title">Cargar presupuesto</h1>
        <p className="ppto-upload-help">
          Sube el Excel mensual de presupuesto (<code>PPTO MES AÑO.xlsx</code>) y, si quieres, el de online
          (<code>PPTO MES AÑO - ONLINE.xlsx</code>). Puedes añadir varios ficheros con el mismo formato, por
          ejemplo uno aparte con una tienda: sus importes se suman. Se cargan las tiendas COMP, NO COMP y
          FRANQ. según la última clasificación de ventas, y el centro I000 como E-COMMERCE.
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        <section className="ppto-upload-step">
          <h2 className="ppto-upload-step-title">1. Fichero</h2>
          <Form.Group controlId="pptoFiles">
            <Form.Control
              ref={this.fileInputRef}
              type="file"
              accept=".xlsx"
              multiple
              onChange={this.handleFiles}
              disabled={uploading || running}
            />
          </Form.Group>
          {files.length > 0 && (
            <ul className="ppto-upload-files">
              {files.map((file) => (
                <li key={file.name}>{file.name} · {mb(file.size)}</li>
              ))}
            </ul>
          )}
          <Button onClick={this.handleUpload} disabled={files.length === 0 || uploading || running || uploaded}>
            {uploading ? "Subiendo..." : uploaded ? "Subido" : "Subir fichero"}
          </Button>
          {(uploading || uploaded) && (
            <ProgressBar
              className="ppto-upload-progress"
              now={total ? (sent / total) * 100 : 0}
              label={`${mb(sent)} / ${mb(total)}`}
              variant={uploaded ? "success" : undefined}
            />
          )}
        </section>

        <section className="ppto-upload-step">
          <h2 className="ppto-upload-step-title">2. Proceso</h2>
          <Form.Check
            id="pptoDryRun"
            type="checkbox"
            label="Simulación: genera el CSV para revisarlo, sin guardar nada en la base de datos"
            checked={dryRun}
            onChange={(event) => this.setState({ dryRun: event.target.checked })}
            disabled={running}
          />
          <div className="ppto-upload-actions">
            <Button variant="success" onClick={this.handleProcess} disabled={files.length === 0 || running || uploading}>
              {running ? "Procesando..." : "Comenzar proceso"}
            </Button>
            {job && job.hasCsv && (
              <Button variant="outline-secondary" onClick={this.handleCsv}>
                Descargar CSV
              </Button>
            )}
            {job && !running && (
              <Button variant="outline-secondary" onClick={this.handleReset}>
                Nueva carga
              </Button>
            )}
          </div>
        </section>

        <section className="ppto-upload-step">
          <h2 className="ppto-upload-step-title">
            Log {status && <Badge variant={status.variant}>{status.text}</Badge>}
          </h2>
          {this.renderResult()}
          <div className="ppto-upload-log" ref={this.logRef}>
            {log.length === 0 ? (
              <span className="ppto-upload-log-empty">Aquí aparecerá el progreso de la subida y del proceso.</span>
            ) : (
              log.map((line, index) => (
                <div key={index} className={`ppto-upload-log-${line.level}`}>
                  <span className="ppto-upload-log-time">{line.time}</span> {line.msg}
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    );
  }
}

export default PptoUpload;
